import {
  useState, useMemo, useEffect, useCallback,
} from "react";
import type { Expense } from "@/features/vouchers";
import { formatDate } from "@/shared/utils/format.util";

type UseExpenseSearchProps = {
  expenses: Expense[]
  resetTrigger?: string | number
};

type UseExpenseSearchReturn = {
  searchAnExpense: string
  filteredExpenses: Expense[]
  handleSearchChange: (newSearchAnExpense: string) => void
};

export const useExpenseSearch = ({ expenses, resetTrigger }: UseExpenseSearchProps): UseExpenseSearchReturn => {
  const [searchAnExpense, setSearchAnExpense] = useState("");

  const normalizeSearchAnExpense = (term: string): string => {
    return term.replace(/,/g, ".");
  };

  const filteredExpenses = useMemo(() => {
    if (!searchAnExpense.trim()) {
      return expenses;
    }

    const searchLower = normalizeSearchAnExpense(searchAnExpense.toLowerCase());

    return expenses.filter((expense: Expense) => {
      const searchableFields = [
        expense.id,
        expense.merchant_name || "",
        expense.amount ? expense.amount.toFixed(2) : "",
        expense.total_transaction_amount ? expense.total_transaction_amount.toFixed(2) : "",
        expense.authorization_time ? formatDate(expense.authorization_time) : "",
        expense.status || "",
      ];

      return searchableFields.some((field) =>
        normalizeSearchAnExpense(field.toLowerCase()).includes(searchLower),
      );
    });
  }, [expenses, searchAnExpense]);

  useEffect(() => {
    setSearchAnExpense("");
  }, [resetTrigger]);

  const handleSearchChange = useCallback((newSearchAnExpense: string) => {
    setSearchAnExpense(newSearchAnExpense);
  }, []);

  return {
    searchAnExpense,
    filteredExpenses,
    handleSearchChange,
  };
};
